import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import AssemblyDashboard from '../../components/assembly/AssemblyDashboard';
import { useAssembly } from '../../context/AssemblyContext';
import { useAuth } from '../../context/AuthContext';
import { Assembly } from '../../types/assembly';
import { assemblyService } from '../../services/assemblyService';
import { getAssemblyTypeLabel, getAssemblyStatusLabel, getAssemblyModalityLabel, getAssemblyStatusBadgeClass } from '../../utils/assemblyLabels';
import { formatAssemblyCalendarDateEs, formatAssemblyTimeEs, getAssemblyDateTimeMillis } from '../../utils/assemblyDate';
import LoadingState from '../../components/common/LoadingState';

const AdminDashboardPage: React.FC = () => {
  const { currentAssemblyId } = useAssembly();
  const { user } = useAuth();
  const [scheduledAssemblies, setScheduledAssemblies] = useState<Assembly[]>([]);
  const [isLoadingScheduled, setIsLoadingScheduled] = useState(false);

  const formatAssemblyDate = (dateString: string) => {
    return formatAssemblyCalendarDateEs(dateString, dateString);
  };

  const formatAssemblyTime = (timeString: string) => {
    return formatAssemblyTimeEs(timeString, '--:--');
  };

  useEffect(() => {
    const loadScheduledAssemblies = async () => {
      if (currentAssemblyId) return;

      setIsLoadingScheduled(true);
      try {
        const response = await assemblyService.getAssemblies({ limit: 50 });
        const scheduled = (response.assemblies || [])
          .filter((assembly) => assembly.status === 'scheduled')
          .sort((a, b) => {
            const aDateTime = getAssemblyDateTimeMillis(a.scheduledDate, a.startTime);
            const bDateTime = getAssemblyDateTimeMillis(b.scheduledDate, b.startTime);
            return aDateTime - bDateTime;
          });

        setScheduledAssemblies(scheduled);
      } catch (error) {
        console.error('Error cargando asambleas programadas para admin:', error);
      } finally {
        setIsLoadingScheduled(false);
      }
    };

    loadScheduledAssemblies();
  }, [currentAssemblyId]);

  if (!currentAssemblyId) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600 mt-2">
                Vista general, gestión en tiempo real y próximas asambleas programadas.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 mb-6 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">
                No hay asamblea activa
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Puedes revisar y preparar las asambleas programadas desde aquí.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Asambleas programadas</h3>
                <span className="text-sm text-gray-500">{scheduledAssemblies.length} disponibles</span>
              </div>

              {isLoadingScheduled ? (
                <LoadingState />
              ) : scheduledAssemblies.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                  <h4 className="text-base font-medium text-gray-900">No hay asambleas programadas por ahora</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Cuando se creen nuevas asambleas programadas, las verás aquí automáticamente.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {scheduledAssemblies.map((assembly) => (
                    <div key={assembly.id} className="rounded-lg border border-gray-200 bg-white p-5">
                      <div className="flex items-start justify-between gap-3">
                        <h4 className="text-base font-semibold text-gray-900 leading-tight">{assembly.title}</h4>
                        <span className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getAssemblyStatusBadgeClass(assembly.status)}`}>
                          {getAssemblyStatusLabel(assembly.status)}
                        </span>
                      </div>

                      {assembly.description && (
                        <p className="mt-2 text-sm text-gray-600 line-clamp-2">{assembly.description}</p>
                      )}

                      <div className="mt-4 space-y-2 text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">Fecha:</span>
                          <span className="capitalize">{formatAssemblyDate(assembly.scheduledDate)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">Hora:</span>
                          <span>{formatAssemblyTime(assembly.startTime)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">Modalidad:</span>
                          <span>{getAssemblyModalityLabel(assembly.isVirtual)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">Tipo:</span>
                          <span>{getAssemblyTypeLabel(assembly.type)}</span>
                        </div>
                        {assembly.isVirtual ? (
                          <div className="flex items-start gap-2">
                            <span className="font-medium text-gray-900">Enlace:</span>
                            {assembly.meetingLink ? (
                              <a
                                href={assembly.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline break-all"
                              >
                                Conectarse ahora
                              </a>
                            ) : (
                              <span className="text-amber-700">Pendiente por definir</span>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-start gap-2">
                            <span className="font-medium text-gray-900">Lugar:</span>
                            <span>{assembly.location || 'Pendiente por definir'}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const handleAssemblySelect = (assembly: Assembly | null) => {
    // Esta función se llama si el usuario cambia de asamblea desde el dashboard
    window.location.reload();
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-2">
              Vista general y gestión de la asamblea activa en tiempo real.
            </p>
          </div>

          <AssemblyDashboard
            assemblyId={currentAssemblyId}
            userRole={user?.role === 'admin' || user?.role === 'manager' ? user.role : 'owner'}
            onAssemblySelect={handleAssemblySelect}
          />
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
